import { unwrap } from "@sagot/api-client";
import { CONNECTOR_DELIVERY_EVENT } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, ErrorState, Icon, Select, TableSkeletonRows, toast, type IconName } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, wsKeys, type DeliveryOut, type WebhookOut } from "../../shared";
import { deliveryTone, hostOf, isFailedDelivery, type DeliveryTone } from "../settings-model";
import { useDeliveries, useInvalidate } from "../use-settings";
import { Gate, useCanManage, TableScroll } from "./panel-kit";

const COLUMNS = 6;
const TONE_ICON: Readonly<Record<DeliveryTone, IconName>> = { success: "check-circle", warning: "clock", danger: "x-circle", neutral: "circle" };

/** What the receiver answered to a delivery that did not go through, and whether it will be tried again. */
function ReceiverAnswer({ delivery, tone, quiet = false }: { delivery: DeliveryOut; tone: DeliveryTone; quiet?: boolean }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const next = delivery.status === "retrying" && delivery.next_retry_at ? t("settings.webhooks.nextRetry", { time: format.dateTime(delivery.next_retry_at) }) : null;
  const final = delivery.status === "failed" && delivery.attempt < delivery.max_attempts ? t("settings.webhooks.notRetried") : null;
  const note = quiet ? null : (next ?? final);
  return (
    <tr data-testid="receiver-answer">
      <td colSpan={COLUMNS} className="pt-0">
        <div className="flex items-start gap-2 text-xs text-fg-muted">
          <Icon name={TONE_ICON[tone]} className="mt-0.5 size-3.5 shrink-0" />
          <div className="min-w-0 space-y-1">
            <p>
              <span className="font-semibold">{t("settings.webhooks.receiverSaid")}:</span> <code className="break-words font-mono">{delivery.response_excerpt}</code>
            </p>
            {note ? <p>{note}</p> : null}
          </div>
        </div>
      </td>
    </tr>
  );
}

function DeliveryRow({ delivery, host, onRedeliver, busy, manage }: { delivery: DeliveryOut; host: string; onRedeliver: () => void; busy: boolean; manage: ReturnType<typeof useCanManage> }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const tone = deliveryTone(delivery.status, delivery.status_code);
  const retry = delivery.attempt > 1 || delivery.status === "retrying" ? ` · ${t("settings.webhooks.retryOf", { attempt: delivery.attempt, max: delivery.max_attempts })}` : "";
  const problem = delivery.status !== "succeeded" && Boolean(delivery.response_excerpt);
  // A connector request is logged, not queued: there is nothing to send again.
  const connectorLog = delivery.event === CONNECTOR_DELIVERY_EVENT;
  return (
    <>
      <tr className={problem ? "[&>td]:border-b-0" : undefined}>
        <td className={connectorLog ? "text-xs font-semibold" : "font-mono text-xs"}>{connectorLog ? t("settings.webhooks.connectorRequest") : delivery.event}</td>
        <td className="text-fg-muted">{host}</td>
        <td>
          <Badge tone={tone} className="font-mono">
            <Icon name={TONE_ICON[tone]} className="size-3" />
            {delivery.status_code ?? t(`settings.webhooks.status.${delivery.status}`, { defaultValue: delivery.status })}
          </Badge>
        </td>
        <td className="text-right tabular-nums text-fg-muted">{delivery.duration_ms === null ? "—" : t("settings.webhooks.ms", { value: format.number(delivery.duration_ms) })}</td>
        <td className="whitespace-nowrap text-right text-fg-subtle">
          {format.dateTime(delivery.created_at)}
          {retry}
        </td>
        <td className="text-right">
          {connectorLog ? null : <Gate reason={manage.reason}>
            <Button variant="ghost" size="sm" disabled={!manage.allowed || busy} onClick={onRedeliver} aria-label={t("settings.webhooks.redeliverOne", { event: delivery.event })}>
              <Icon name="rotate-ccw" />
              <span className="hidden sm:inline">{t("settings.webhooks.redeliver")}</span>
            </Button>
          </Gate>}
        </td>
      </tr>
      {problem ? <ReceiverAnswer delivery={delivery} tone={tone} quiet={connectorLog} /> : null}
    </>
  );
}

/** Recent deliveries of one endpoint (there's no workspace-wide feed); redeliver one or all failed. */
export function DeliveriesCard({ webhooks }: { webhooks: readonly WebhookOut[] }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("webhooks.manage");
  const [selected, setSelected] = useState<string | null>(null);
  const endpoint = webhooks.find((w) => w.id === selected) ?? webhooks[0] ?? null;
  const deliveries = useDeliveries(endpoint?.id ?? null);
  const [busy, setBusy] = useState(false);
  const failed = (deliveries.data ?? []).filter((d) => d.event !== CONNECTOR_DELIVERY_EVENT && isFailedDelivery(d.status, d.status_code));

  const redeliver = async (ids: readonly string[]) => {
    setBusy(true);
    try {
      for (const id of ids) await unwrap(api.POST("/v1/webhook-deliveries/{delivery_id}/redeliver", { params: { path: { delivery_id: id } } }));
      toast.info(t("settings.webhooks.redelivering", { count: ids.length }));
      if (endpoint) await invalidate(wsKeys.deliveries(wsId, endpoint.id));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  if (!endpoint) return null;
  return (
    <section className="card overflow-hidden" aria-labelledby="deliveries-title">
      <div className="card-header flex-wrap gap-2">
        <h2 id="deliveries-title" className="card-title">
          {t("settings.webhooks.deliveries")}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {webhooks.length > 1 ? (
            <Select className="h-8 w-56 text-xs" aria-label={t("settings.webhooks.endpointFilter")} value={endpoint.id} onChange={(event) => { setSelected(event.target.value); }}>
              {webhooks.map((w) => (
                <option key={w.id} value={w.id}>
                  {hostOf(w.url)}
                </option>
              ))}
            </Select>
          ) : null}
          <Gate reason={manage.reason}>
            <Button variant="ghost" size="sm" disabled={!manage.allowed || failed.length === 0} loading={busy} onClick={() => void redeliver(failed.map((d) => d.id))}>
              <Icon name="refresh-cw" />
              {t("settings.webhooks.retryFailed")}
            </Button>
          </Gate>
        </div>
      </div>
      {deliveries.isError ? (
        <ErrorState headingLevel={3} title={t("settings.webhooks.deliveriesError")} onRetry={() => void deliveries.refetch()} retryLabel={t("common.retry")} />
      ) : deliveries.data?.length === 0 ? (
        <EmptyState icon="send" headingLevel={3} title={t("settings.webhooks.noDeliveriesTitle")} description={t("settings.webhooks.noDeliveriesBody")} />
      ) : (
        <TableScroll label={t("settings.webhooks.deliveries")}>
          <table className="table">
            <thead>
              <tr>
                <th>{t("settings.webhooks.colEvent")}</th>
                <th>{t("settings.webhooks.colEndpoint")}</th>
                <th>{t("settings.webhooks.colStatus")}</th>
                <th className="text-right">{t("settings.webhooks.colDuration")}</th>
                <th className="text-right">{t("settings.webhooks.colTime")}</th>
                <th>
                  <span className="sr-only">{t("settings.colActions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveries.isPending ? (
                <TableSkeletonRows rows={4} columns={COLUMNS} />
              ) : (
                deliveries.data.map((d) => <DeliveryRow key={d.id} delivery={d} host={hostOf(endpoint.url)} manage={manage} busy={busy} onRedeliver={() => void redeliver([d.id])} />)
              )}
            </tbody>
          </table>
        </TableScroll>
      )}
    </section>
  );
}
